import React, {useMemo, useState} from 'react';

import {Icon, Tab} from '@albert-io/atomic';
import ManageRosterContext from 'client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.context';
import manageRosterModalCollection from 'client/Classrooms/Classroom/Teacher/ManageRoster/manageRosterModalCollection';
import PeopleContext from 'client/Classrooms/Classroom/Teacher/People/people.context';
import LoadingIndicator from 'generic/LoadingIndicator.react';

import {ErrorRetryState} from '../../components/ErrorRetryState';
import {StudentsTab} from './StudentsTab';
import type {StudentSegment} from './StudentsTab';
import {TeachersTab} from './TeachersTab';
import {usePeopleRoster} from './usePeopleRoster';

type PeopleTab = 'students' | 'teachers';

// The reused legacy roster modals/dropdowns read these untyped contexts.
const TypedManageRosterContext = ManageRosterContext as unknown as React.Context<unknown>;
const TypedPeopleContext = PeopleContext as unknown as React.Context<unknown>;
// email | bulkResend | classCode | googleClassroom | LMS — student invite flows, rendered on demand.
const ROSTER_MODALS = manageRosterModalCollection as Record<
  string,
  React.ComponentType<{handleClose: () => void}>
>;

interface Props {
  classroomId: string;
}

export const People = ({classroomId}: Props) => {
  const roster = usePeopleRoster(classroomId);
  const [activeTab, setActiveTab] = useState<PeopleTab>('students');
  const [studentSegment, setStudentSegment] = useState<StudentSegment>('enrolled');
  // Drives the shared student-invite modals (mirrors the legacy ManageRoster container).
  const [modalToShow, setModalToShow] = useState('');

  const {
    classroom,
    isReady,
    hasError,
    reload,
    softReload,
    queries,
    currentTeacherId,
    students,
    invitations
  } = roster;

  // setIsReady(false) is how the reused flows signal "I changed data". Refresh in place (softReload)
  // rather than reload(): reload() drops isReady and remounts the whole view behind a spinner — a
  // jarring "page refresh" even when nothing changed (e.g. cancelling the invite-by-email modal).
  const manageRosterContext = useMemo(
    () => ({
      queries: {
        classroom: queries.classroom,
        invitations: queries.invitations,
        students: queries.students
      },
      classroom,
      invitations,
      students,
      isReady,
      modalToShow,
      setIsReady: (value: boolean) => {
        if (!value) {
          softReload();
        }
      },
      setModalToShow
    }),
    [queries, classroom, invitations, students, isReady, modalToShow, softReload]
  );

  const peopleContext = useMemo(
    () => ({classroom, queries: {coteachers: queries.coteachers}, currentTeacherId}),
    [classroom, queries, currentTeacherId]
  );

  if (!isReady) {
    return <LoadingIndicator />;
  }

  if (hasError || typeof classroom?.getId !== 'function') {
    return (
      <div className='pt-x4'>
        <ErrorRetryState copy='We couldn’t load this class’s roster.' onRetry={reload} />
      </div>
    );
  }

  const ModalRenderer = modalToShow ? ROSTER_MODALS[modalToShow] : null;

  return (
    <TypedManageRosterContext.Provider value={manageRosterContext}>
      <TypedPeopleContext.Provider value={peopleContext}>
        <div className='flex flex-col'>
          <nav
            aria-label='People views'
            className='-mx-x4 border-border-primary px-x4 flex border-b'
          >
            <Tab
              as='button'
              isActive={activeTab === 'students'}
              onClick={() => setActiveTab('students')}
            >
              <Icon className='mr-x1' icon='users' iconStyle='regular' size='inherit' />
              Students
            </Tab>
            <Tab
              as='button'
              isActive={activeTab === 'teachers'}
              onClick={() => setActiveTab('teachers')}
            >
              <Icon className='mr-x1' icon='person-chalkboard' iconStyle='regular' size='inherit' />
              Teachers
            </Tab>
          </nav>
          {activeTab === 'students' ? (
            <StudentsTab
              classroom={classroom}
              invitations={invitations}
              onSegmentChange={setStudentSegment}
              onShowModal={setModalToShow}
              segment={studentSegment}
              students={students}
            />
          ) : (
            <TeachersTab coteachers={roster.coteachers} currentTeacherId={currentTeacherId} />
          )}
        </div>
        {ModalRenderer && <ModalRenderer handleClose={() => setModalToShow('')} />}
      </TypedPeopleContext.Provider>
    </TypedManageRosterContext.Provider>
  );
};
